import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useSmartPoll } from '../hooks/useSmartPoll';

// الموافقة هنا وحدها: رمز الموصل لا يبلغ هذه المسارات، فلا يوافق Claude عن نفسه
export default function McpApprovals() {
  const { api } = useAuth();
  const toast = useToast();
  const [approvals, setApprovals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => api('/me/mcp-approvals').then(setApprovals).catch(() => {}), [api]);
  useEffect(() => { load(); }, []);
  useSmartPoll(load, { activeMs: 8000, idleMs: 20000 });

  const decide = async (id, action) => {
    setBusy(true);
    try {
      const r = await api(`/me/mcp-approvals/${id}/${action}`, { method: 'POST' });
      toast.success(r.message);
      if (action === 'approve') setDone(true);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); load(); }
  };

  // المعرّفات المعروضة وحدها: ما وصل بعد فتح الصفحة لم يُرَ بعد
  const approveAll = async () => {
    setBusy(true);
    try {
      const r = await api('/me/mcp-approvals/approve', { method: 'POST', body: JSON.stringify({ ids: approvals.map(a => a.id) }) });
      toast.success(r.message);
      setDone(true);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); load(); }
  };

  const minutesLeft = (iso) => Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));

  if (approvals === null) return <div className="loader">جاري التحميل...</div>;

  return (
    <div style={{ maxWidth: 760 }}>
      <h4 style={{ marginBottom: 8 }}>موافقات الموصل</h4>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0, lineHeight: 1.7 }}>
        ما ينتظر موافقتك من Claude: مراسلة من هم خارج الفريق، والمال، والنشر، والحذف — وكل ما يغيّر في المنصّة إن كان الربط «تنفيذاً بموافقة».
        إعداد الربط ومستواه في <Link to="/profile">الملف الشخصي</Link>.
      </p>

      {done && (
        <div role="status" style={{ background: 'var(--c-blue-100)', border: '1px solid var(--c-blue-300)', padding: 12, borderRadius: 6, margin: '12px 0', lineHeight: 1.7 }}>
          تمت الموافقة. عُد إلى Claude واكتب: <strong>وافقت، تابع</strong> — فينفّذ ما وافقت عليه مرّة واحدة.
        </div>
      )}

      {approvals.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>لا طلبات معلّقة.</div>
      ) : (
        <>
          {approvals.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
              <span style={{ fontSize: 13 }}>{approvals.length} طلبات معلّقة — راجعها ثم وافق دفعةً واحدة:</span>
              <button className="btn btn-primary" disabled={busy} onClick={approveAll}>موافقة على الكل ({approvals.length})</button>
            </div>
          )}
          {approvals.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>#{a.id} — {a.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
                {a.token && `من الرمز: ${a.token} · `}تنتهي خلال {minutesLeft(a.expires_at)} دقيقة
              </div>
              <dl style={{ margin: 0, fontSize: 14, lineHeight: 1.8 }}>
                {a.summary.map((line, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <dt style={{ color: 'var(--text-muted)', minWidth: 110 }}>{line.label}</dt>
                    <dd style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>{line.value}</dd>
                  </div>
                ))}
              </dl>
              <details style={{ marginTop: 6 }}>
                <summary style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>التفاصيل التقنية</summary>
                <pre style={{ direction: 'ltr', textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: 'var(--gray-50)', padding: 8, borderRadius: 4, margin: '6px 0 0' }}>
                  {a.tool}{'\n'}{JSON.stringify(a.args, null, 2)}
                </pre>
              </details>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} aria-label={`موافقة على الطلب ${a.id}`} onClick={() => decide(a.id, 'approve')}>موافقة</button>
                <button className="btn btn-outline" disabled={busy} aria-label={`رفض الطلب ${a.id}`} onClick={() => decide(a.id, 'reject')}>رفض</button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
